import { TaskQueue } from "../TaskQueue.mjs";
import { ReconnectError } from "../ReconnectError.mjs";
import { TransientNetworkError } from "../TransientNetworkError.mjs";

//#region src/sync/SyncEngine.ts
function invertibleMutationToMutation(invertible) {
	return {
		id: invertible.id,
		ops: invertible.ops.map((op) => {
			if (op.type === "set") return {
				type: "set",
				collection: op.collection,
				value: op.value
			};
			else return {
				type: "remove",
				collection: op.collection,
				id: op.id
			};
		})
	};
}
var SyncEngine = class {
	syncQueue;
	pendingMutations = [];
	remote;
	logger;
	handleRollback;
	applyPatchAt;
	clientId;
	cookie;
	disconnectFromRemote;
	scanWindow = [];
	pullRetryCount = 0;
	pushRetryCount = 0;
	maxRetries = 5;
	baseRetryDelay = 1e3;
	onTransientError;
	onTransientErrorRecovered;
	onMaxRetriesExceeded;
	constructor(args) {
		this.logger = args.logger;
		this.remote = args.remote;
		this.handleRollback = args.handleRollback;
		this.onTransientError = args.onTransientError;
		this.onTransientErrorRecovered = args.onTransientErrorRecovered;
		this.onMaxRetriesExceeded = args.onMaxRetriesExceeded;
		this.applyPatchAt = args.applyPatchAt;
		this.clientId = args.clientId;
		this.syncQueue = new TaskQueue({
			pull: () => this.pull(),
			push: () => this.push(),
			connect: () => this.connectInternal(),
			disconnect: () => this.disconnectInternal()
		}, args.syncInterval);
		if (args.autoConnect) this.connect().catch((error) => {
			this.logger.error("Error connecting to remote", error);
		});
	}
	async connect() {
		this.logger.info("connect called");
		await this.syncQueue.enqueue("connect");
	}
	async disconnect() {
		this.logger.info("disconnect called");
		await this.syncQueue.enqueue("disconnect");
	}
	async connectInternal() {
		this.logger.info("connectInternal called");
		this.disconnectFromRemote = await this.remote.connect({
			clientId: this.clientId,
			poke: () => {
				this.queuePull();
			}
		});
		this.queuePull();
	}
	async disconnectInternal() {
		this.logger.info("disconnectInternal called");
		await this.disconnectFromRemote?.();
		this.disconnectFromRemote = void 0;
	}
	subscribe(query) {
		this.logger.info("subscribe called", { scanWindowSize: this.scanWindow.length + 1 });
		this.scanWindow.push(query);
		this.queuePull();
		return () => {
			this.logger.info("unsubscribe called", { scanWindowSize: this.scanWindow.length - 1 });
			this.scanWindow.splice(this.scanWindow.indexOf(query), 1);
		};
	}
	queuePull() {
		this.logger.info("queuePull called");
		return this.syncQueue.enqueue("pull");
	}
	async sleep(ms) {
		return await new Promise((resolve) => setTimeout(resolve, ms));
	}
	calculateBackoffDelay(retryCount) {
		const delay = this.baseRetryDelay * Math.pow(2, retryCount);
		return delay + Math.random() * .3 * delay;
	}
	isTransientError(error) {
		return error instanceof ReconnectError || error instanceof TransientNetworkError;
	}
	async handleTransientError(fn, operation, retryCount) {
		try {
			const result = await fn();
			const hadRetries = retryCount > 0;
			if (operation === "pull") this.pullRetryCount = 0;
			else this.pushRetryCount = 0;
			if (hadRetries) this.onTransientErrorRecovered?.({
				operation,
				totalRetries: retryCount
			});
			return result;
		} catch (error) {
			const isTransient = this.isTransientError(error);
			if (isTransient && retryCount < this.maxRetries) {
				const delay = this.calculateBackoffDelay(retryCount);
				this.onTransientError?.({
					error,
					operation,
					retryCount: retryCount + 1,
					maxRetries: this.maxRetries,
					retryDelayMs: delay
				});
				if (error instanceof ReconnectError) {
					this.logger.warn(`${operation} failed with ReconnectError. Reconnecting... (attempt ${retryCount + 1}/${this.maxRetries})`);
					await this.disconnectInternal();
					await this.connectInternal();
				} else if (error instanceof TransientNetworkError) {
					this.logger.warn(`${operation} failed with transient error. Retrying in ${Math.round(delay)}ms (attempt ${retryCount + 1}/${this.maxRetries})`, error);
					await this.sleep(delay);
				}
				return await this.handleTransientError(fn, operation, retryCount + 1);
			}
			if (isTransient && retryCount >= this.maxRetries) {
				this.logger.error(`${operation} failed after ${this.maxRetries} retries with transient error. Disconnecting.`, error);
				this.onMaxRetriesExceeded?.({
					error,
					operation
				});
				await this.disconnectInternal();
			}
			throw error;
		}
	}
	async pull() {
		this.logger.info("pull called", { scanWindowLength: this.scanWindow.length });
		if (this.scanWindow.length === 0) return;
		const { cookie, patch, lastMutationId } = await this.handleTransientError(() => this.remote.pull({
			clientId: this.clientId,
			cookie: this.cookie,
			scanWindow: this.scanWindow
		}), "pull", this.pullRetryCount);
		this.cookie = cookie;
		this.applyPatchAt({
			patch,
			lastMutationId
		});
	}
	queuePush(mutation) {
		this.logger.info("queuePush called", {
			mutationId: mutation.id,
			pendingMutationsCount: this.pendingMutations.length + 1
		});
		this.pendingMutations.push(mutation);
		return this.syncQueue.enqueue("push");
	}
	async push() {
		this.logger.info("push called", { pendingMutationsCount: this.pendingMutations.length });
		if (this.pendingMutations.length === 0) return;
		const mutationsToPush = this.pendingMutations;
		this.pendingMutations = [];
		try {
			const mutations = mutationsToPush.map(invertibleMutationToMutation);
			await this.handleTransientError(() => this.remote.push({
				mutations,
				clientId: this.clientId
			}), "push", this.pushRetryCount);
		} catch (error) {
			this.logger.error("Error applying mutation", error);
			this.handleRollback(mutationsToPush);
		}
	}
};

//#endregion
export { SyncEngine };